import { fuelTypesList } from '../../../../../resources/base/models/vehicleTypes';
import { formatPrice } from '../../../../../services/common/format';
import {
  getJatoReference3FromVehicle,
  getMilesCatalogVehicleIdFromVehicle,
} from '../../../../../services/common/miscUtils';
import { OptionsItem } from '../../../../../services/common/miscUtils.types';
import { getDateWithMonthOffset } from '../../../../utils';
import { parseStringToDate } from '../../../utils/formatDate';

export const modelYearList = (
  list: Array<VehicleIntegration.Components.Schemas.CatalogVehicleDataResponse>
): Array<OptionsItem> => {
  const uniqueYears = new Set<number>();

  list?.forEach((item) => {
    const year = item?.model?.year;
    if (year !== undefined) {
      uniqueYears.add(year);
    }
  });

  return Array.from(uniqueYears)
    .sort((a, b) => b - a)
    .map((year) => ({ value: `${year}`, label: `${year}` }));
};

export const getEngineList = (
  modelyear?: string,
  list?: Array<VehicleIntegration.Components.Schemas.CatalogVehicleDataResponse>
) => {
  const uniqueEngines = new Set<string>();
  const array: any[] = [];

  list?.forEach((item: any) => {
    if (item.model.year === Number(modelyear)) {
      const engine = item?.model.engine?.fuelTypeLabel;
      // eslint-disable-next-line no-unused-expressions
      engine !== undefined &&
        !uniqueEngines.has(engine) &&
        (uniqueEngines.add(engine),
        array.push({
          value: engine,
          label: fuelTypesList.find((item) => item.value === engine)?.label,
        }));
    }
  });

  return array;
};

export const getVehicleList = (
  modelyear?: string,
  engine?: string,
  list?: Array<VehicleIntegration.Components.Schemas.CatalogVehicleDataResponse>
): OptionsItem[] => {
  if (!list || !modelyear || !engine) {
    return [];
  }

  const filteredList = list.filter(
    (vehicle) => vehicle.model?.year === Number(modelyear) && vehicle.model?.engine?.fuelTypeLabel === engine
  );

  const vehiclesDictionary = {} as Record<string, VehicleIntegration.Components.Schemas.CatalogVehicleDataResponse>;

  filteredList.forEach((vehicle) => {
    const key = `${getJatoReference3FromVehicle(vehicle)}-${vehicle.retailPriceAmount?.grossAmount}`;
    const milesVehicleId = Number(getMilesCatalogVehicleIdFromVehicle(vehicle) ?? 0);

    if (!milesVehicleId) return;

    if (vehiclesDictionary[key]) {
      const existingMilesVehicleId = Number(getMilesCatalogVehicleIdFromVehicle(vehiclesDictionary[key]) ?? 0);

      if (milesVehicleId > existingMilesVehicleId) {
        vehiclesDictionary[key] = vehicle;
      }
    } else {
      vehiclesDictionary[key] = vehicle;
    }
  });

  const sortedVehiclesList = Object.values(vehiclesDictionary).sort((vehicleA, vehicleB) => {
    // Primary sorting by model description (alphabetical, ascending)
    const descriptionA = vehicleA.model?.description || '';
    const descriptionB = vehicleB.model?.description || '';
    const descriptionComparison = descriptionA.localeCompare(descriptionB);

    if (descriptionComparison !== 0) return descriptionComparison;

    // Secondary sorting (within groups with the same description) by price (descending)
    const priceA = vehicleA.retailPriceAmount?.grossAmount || 0;
    const priceB = vehicleB.retailPriceAmount?.grossAmount || 0;
    return priceB - priceA;
  });

  return sortedVehiclesList
    .map(({ model, retailPriceAmount, externalVehicleIdentifiers }) => {
      const vehicleId = getMilesCatalogVehicleIdFromVehicle({ externalVehicleIdentifiers })!;

      if (!model?.description) return null;

      return {
        value: vehicleId,
        label: `${model.description} [ ${formatPrice(retailPriceAmount?.grossAmount || 0)} ]`,
      };
    })
    .filter(Boolean) as OptionsItem[];
};

const DEMO_CAR_AGE_BOUNDARY = 6;
const DEMO_CAR_MILEAGE_BOUNDARY = 10000;

/**
 * STOCK and NEW: Check if currMileage ≥ DEMO_CAR_MIN_MILEAGE (10.000km) then trigger popup
 */
export const checkCurrentMileageStockAndNew = (
  currMileage: number,
  setShowDemoCarModal: React.Dispatch<React.SetStateAction<boolean>>
) => {
  if (currMileage >= DEMO_CAR_MILEAGE_BOUNDARY) {
    setShowDemoCarModal(true);
  }
};

/**
 * USED: Return false if currMileage < DEMO_CAR_MIN_MILEAGE (10.000km), true otherwise
 */
export const checkValidCurrentMileageUsed = (currMileage: number | undefined): boolean => {
  if (currMileage !== undefined && currMileage < DEMO_CAR_MILEAGE_BOUNDARY) {
    return false;
  }
  return true;
};

/**
 * STOCK: Check if regDate is older than DEMO_CAR_MIN_MONTHS (6 months) then trigger popup
 */
export const checkCurrentRegDateStock = (
  regDate: string,
  setShowDemoCarModal: React.Dispatch<React.SetStateAction<boolean>>
) => {
  const sixMonthsAgo = getDateWithMonthOffset(DEMO_CAR_AGE_BOUNDARY);
  const selectedDate = parseStringToDate(regDate);

  if (selectedDate < sixMonthsAgo) {
    setShowDemoCarModal(true);
  }
};

/**
 * USED: Return false if regDate is newer than DEMO_CAR_MIN_MONTHS (6 months), true otherwise
 */
export const checkValidCurrentRegDateUsed = (regDate: string): boolean => {
  const sixMonthsAgo = getDateWithMonthOffset(DEMO_CAR_AGE_BOUNDARY);
  const selectedDate = parseStringToDate(regDate);

  if (selectedDate > sixMonthsAgo) {
    return false;
  }
  return true;
};

/**
 * USED: Show popup only if isDemo && both date and mileage are invalid
 */
export const shouldShowUsedDemoCarModal = (
  regDate: string,
  mileage: number | undefined,
  setShowDemoCarModal: React.Dispatch<React.SetStateAction<boolean>>,
  isDemo: boolean
) => {
  const dateValid = checkValidCurrentRegDateUsed(regDate);
  const mileageValid = checkValidCurrentMileageUsed(mileage);

  if (!isDemo || dateValid || mileageValid) return;

  setShowDemoCarModal(true);
};

/**
 * Returns a sorted list of manufacturers, prioritizing specific brands at the top
 * in a predefined order, followed by the rest sorted alphabetically.
 *
 * Priority brands are matched case-insensitively.
 *
 * @param manufacturersList - The full list of manufacturer options to sort.
 * @returns A new array with priority brands first (in specified order),
 *          followed by all remaining manufacturers sorted alphabetically.
 */
export const getSortedManufacturers = (manufacturersList: OptionsItem[]) => {
  const priorityBrands = ['Volkswagen', 'Audi', 'SEAT', 'Skoda', 'Volkswagen Nutzfahrzeuge'];

  const priorityItems: OptionsItem[] = priorityBrands
    .map((name) => manufacturersList.find((item) => item.value.toLowerCase() === name.toLowerCase()))
    .filter((item): item is OptionsItem => !!item);

  const restItems: OptionsItem[] = manufacturersList
    .filter((item) => !priorityBrands.some((p) => p.toLowerCase() === item.value.toLowerCase()))
    .sort((a, b) => a.value.localeCompare(b.value, 'en', { sensitivity: 'base' }));

  return [...priorityItems, ...restItems];
};
